import React, { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { Button, Box, AppBar, Tabs, Tab, Typography, Paper, IconButton } from '@mui/material';
import { 
  Layers, 
  Close,
  Map,
  LayersOutlined
} from '@mui/icons-material';

import { styled } from '@mui/system';
import SwipeableViews from 'react-swipeable-views-react-18-fix';

import LayerSwitcher from './LayerSwitcher';
import LayerSwitcherUserData from './LayerSwitcherUserData';
import BaseMapsUtil from './BaseMapsUtil';

import { innerColor, themeColor, modernDesignSystem } from '../constants/themes';

// Modern Tasarım Sistemi Referansı
const designSystem = modernDesignSystem;

const TabPanel = ({ value, index, children }) => {
  return (
    <div 
      role="tabpanel" 
      hidden={value !== index} 
      id={`tabpanel-${index}`}
      style={{ 
        height: '100%',
        overflow: 'auto'
      }}
    >
      {value === index && (
        <Box 
          p={1.5}
          sx={{ 
            height: '100%',
            overflow: 'auto',
            backgroundColor: designSystem.colors.neutral.gray50,
            '&::-webkit-scrollbar': {
              width: '4px',
            },
            '&::-webkit-scrollbar-track': {
              backgroundColor: designSystem.colors.neutral.gray200,
              borderRadius: '2px',
            },
            '&::-webkit-scrollbar-thumb': {
              backgroundColor: designSystem.colors.primary.light,
              borderRadius: '2px',
              '&:hover': {
                backgroundColor: designSystem.colors.primary.main,
              },
            },
          }}
        >
          {children}
        </Box>
      )}
    </div>
  );
};

const StyledTabs = styled(Tabs)(({ theme }) => ({
  background: `linear-gradient(135deg, ${designSystem.colors.secondary.main} 0%, ${designSystem.colors.tertiary.main} 100%)`,
  borderRadius: '5px 5px 0 0', // Üst köşelerde radius
  overflow: 'hidden',
  '& .MuiTabs-flexContainer': {
    justifyContent: 'center',
  },
  '& .MuiTab-root': {
    backgroundColor: 'transparent',
    color: designSystem.colors.neutral.white,
    minHeight: '50px',
    fontWeight: 600,
    fontSize: '0.85rem',
    transition: 'all 0.3s ease',
    '&:hover': {
      backgroundColor: 'rgba(255, 255, 255, 0.15)',
      transform: 'translateY(-1px)',
    },
    '&.Mui-selected': {
      backgroundColor: designSystem.colors.neutral.white,
      color: designSystem.colors.secondary.dark,
      fontWeight: 700,
    }
  },
  '& .MuiTabs-indicator': {
    display: 'none',
  },
  position: 'relative !important',
  zIndex: 'auto !important',
}));

const ToggleButton = styled(IconButton)(({ open, zindex }) => ({
  background: `linear-gradient(135deg, ${designSystem.colors.primary.main} 0%, ${designSystem.colors.primary.dark} 100%)`,
  color: designSystem.colors.neutral.white,
  width: '52px',
  height: '52px',
  borderRadius: designSystem.borders.radius.medium,
  boxShadow: designSystem.shadows.elevated,
  position: 'fixed',
  top: '90px', // AppBar'dan 5px aşağıda
  right: open ? '440px' : '5px', // Panel açıkken 430px + 5px + 5px = 440px
  zIndex: zindex || 1201, // Dinamik z-index
  transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
  border: `3px solid ${designSystem.colors.primary.surface}`,
  backdropFilter: 'blur(10px)',
  
  // Masaüstü modunda panel açıkken butonu daha şeffaf yap
  opacity: 1,
  
  '@media (min-width: 1025px)': {
    opacity: open ? 0.7 : 1, // Masaüstü modunda panel açıkken daha şeffaf
    '&:hover': {
      opacity: 1,
    }
  },
  
  '&::before': {
    content: '""',
    position: 'absolute',
    top: '-3px',
    left: '-3px',
    right: '-3px',
    bottom: '-3px',
    background: `linear-gradient(135deg, ${designSystem.colors.primary.light} 0%, ${designSystem.colors.secondary.main} 100%)`,
    borderRadius: designSystem.borders.radius.medium,
    zIndex: -1,
    opacity: 0,
    transition: 'opacity 0.3s ease',
  },
  
  '&:hover': {
    background: `linear-gradient(135deg, ${designSystem.colors.primary.dark} 0%, ${designSystem.colors.secondary.dark} 100%)`,
    transform: 'scale(1.05) translateY(-2px)',
    boxShadow: designSystem.shadows.heavy,
    
    '&::before': {
      opacity: 1,
    }
  },
  
  '&:active': {
    transform: 'scale(0.95)',
  },

  // Panel açıkken farklı stil
  ...(open && {
    background: `linear-gradient(135deg, ${designSystem.colors.secondary.main} 0%, ${designSystem.colors.primary.main} 100%)`,
    border: `3px solid ${designSystem.colors.secondary.surface}`,
  }),

  '@media (max-width: 1024px)': {
    right: open ? 'calc(100vw - 60px)' : '5px',
  },
  '@media (max-width: 768px)': {
    right: open ? 'calc(100vw - 56px)' : '5px',
    width: '48px',
    height: '48px',
  }
}));

const AnimatedPanel = () => {
  const layers = useSelector((state) => state.layers);
  // Ekran boyutunu algıla
  const [isTabletMode, setIsTabletMode] = useState(window.innerWidth <= 1024);
  const [open, setOpen] = useState(!isTabletMode); // Tablet modunda değilse varsayılan açık
  const [value, setValue] = useState(0);
  const [lastOpenedPanel, setLastOpenedPanel] = useState('left'); // Hangi panel en son açıldı

  // Ekran boyutu değişikliklerini izle
  useEffect(() => {
    const handleResize = () => {
      const newIsTabletMode = window.innerWidth <= 1024;
      setIsTabletMode(newIsTabletMode);
      
      // Tablet modundan masaüstüne geçerken paneli aç
      if (!newIsTabletMode && isTabletMode) {
        setOpen(true);
      }
      // Masaüstünden tablet moduna geçerken paneli kapat
      else if (newIsTabletMode && !isTabletMode) {
        setOpen(false);
      }
    };

    window.addEventListener('resize', handleResize);
    
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, [isTabletMode]);

  const handleToggle = () => {
    const newOpenState = !open;
    setOpen(newOpenState);
    
    // Panel açılıyorsa, bu paneli en son açılan olarak işaretle
    if (newOpenState) {
      setLastOpenedPanel('right');
      localStorage.setItem('lastOpenedPanel', 'right');
      // Diğer panele bildir
      window.dispatchEvent(new CustomEvent('panelChanged', { detail: 'right' }));
    }
  };

  // Diğer panelin durumunu kontrol et
  useEffect(() => {
    const handlePanelChange = (event) => {
      setLastOpenedPanel(event.detail);
    };

    const handleStorageChange = () => {
      const storedLastPanel = localStorage.getItem('lastOpenedPanel');
      if (storedLastPanel) {
        setLastOpenedPanel(storedLastPanel);
      }
    };

    // Custom event ve localStorage değişikliklerini dinle
    window.addEventListener('panelChanged', handlePanelChange);
    window.addEventListener('storage', handleStorageChange);
    
    // Sayfa yüklendiğinde kontrol et
    handleStorageChange();

    return () => {
      window.removeEventListener('panelChanged', handlePanelChange);
      window.removeEventListener('storage', handleStorageChange);
    };
  }, []);

  // Z-index değerini dinamik olarak hesapla
  const isTopPanel = lastOpenedPanel === 'right';
  const panelZIndex = isTopPanel ? 1300 : 1200;
  const buttonZIndex = isTopPanel ? 1301 : 1201;

  const handleChange = (event, newValue) => {
    setValue(newValue);
  };

  const handleChangeIndex = (index) => {
    setValue(index);
  };

  return (
    <div>
      <ToggleButton
        open={open}
        zindex={buttonZIndex}
        onClick={handleToggle}
        aria-label={open ? "Paneli Kapat" : "Katman Panelini Aç"}
      >
        {open ? <Close /> : <Layers />}
      </ToggleButton>
      
      <Paper
        className="right-navigator-panel"
        elevation={0}
        sx={{
          position: 'fixed',
          top: '90px', // AppBar'dan 5px aşağıda
          right: open ? '5px' : '-430px', // Sağdan 5px mesafe
          height: '53.5vh', // Sol panel ile aynı yükseklik
          width: '430px',
          backgroundColor: designSystem.colors.neutral.white,
          transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
          zIndex: panelZIndex, // Dinamik z-index
          display: 'flex',
          flexDirection: 'column',
          borderRadius: '5px', // Tüm köşelerde 5px border radius
          boxShadow: open ? designSystem.shadows.elevated : 'none',
          border: `1px solid ${designSystem.colors.neutral.gray200}`,
          overflow: 'hidden', // İçeriğin border radius'a uyması için
          
          // Responsive ayarlar
          '@media (max-width: 1024px)': {
            width: '100vw',
            right: open ? '0' : '-100vw',
            borderRadius: 0,
          },
          '@media (max-width: 768px)': {
            width: '100vw',
            right: open ? '0' : '-100vw',
            borderRadius: 0,
          }
        }}
      >
        {/* Modern Header */}
        <Box
          sx={{
            background: `linear-gradient(135deg, ${designSystem.colors.primary.main} 0%, ${designSystem.colors.primary.dark} 100%)`,
            borderRadius: '5px 5px 0 0', // Üst köşelerde radius
            position: 'relative',
            overflow: 'hidden',
            boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)', // Alta doğru gölge efekti
            zIndex: 10, // İçeriğin üstünde kalması için
            '&::before': {
              content: '""',
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              background: 'rgba(255, 255, 255, 0.1)',
              backdropFilter: 'blur(10px)',
            }
          }}
        >
          <StyledTabs
            value={value}
            onChange={handleChange}
            aria-label="Katman Seçenekleri"
            variant="fullWidth"
          >
            <Tab label="🗂️ Katmanlar" />
            <Tab label="🗺️ Haritalar" />
          </StyledTabs>
        </Box>

        <SwipeableViews 
          index={value} 
          onChangeIndex={handleChangeIndex}
          style={{ 
            flexGrow: 1,
            height: 'calc(53.5vh - 50px)', // Sol panel ile aynı yükseklik - tab bar yüksekliği
            overflow: 'hidden'
          }}
          containerStyle={{
            height: '100%'
          }}
        >
          <TabPanel value={value} index={0}>
            <LayerSwitcher layers={layers} />
          </TabPanel>
          <TabPanel value={value} index={1}>
            <BaseMapsUtil />
          </TabPanel>
        </SwipeableViews>
      </Paper>
    </div>
  );
};

export default AnimatedPanel;
